/**
 * Trunnion AI - Dagre Auto-Layout Engine
 * Automatic layout for workflow graphs using dagre.
 */

import dagre from 'dagre';
import type { Node, Edge } from '@xyflow/react';

interface LayoutOptions {
  direction?: 'TB' | 'LR' | 'BT' | 'RL';
  nodeWidth?: number;
  nodeHeight?: number;
  rankSep?: number;
  nodeSep?: number;
  edgeSep?: number;
  marginX?: number;
  marginY?: number;
}

const DEFAULT_OPTIONS: Required<LayoutOptions> = {
  direction: 'TB',
  nodeWidth: 280,
  nodeHeight: 120,
  rankSep: 80,
  nodeSep: 50,
  edgeSep: 30,
  marginX: 40,
  marginY: 40,
};

/**
 * Apply dagre auto-layout to a set of nodes and edges.
 * Returns new nodes with updated positions.
 */
export function applyDagreLayout<N extends Record<string, unknown>, E extends Record<string, unknown>>(
  nodes: Node<N>[],
  edges: Edge<E>[],
  options?: LayoutOptions,
): Node<N>[] {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: opts.direction,
    ranksep: opts.rankSep,
    nodesep: opts.nodeSep,
    edgesep: opts.edgeSep,
    marginx: opts.marginX,
    marginy: opts.marginY,
  });

  // Add nodes to dagre
  for (const node of nodes) {
    const width = node.measured?.width ?? opts.nodeWidth;
    const height = node.measured?.height ?? opts.nodeHeight;
    dagreGraph.setNode(node.id, { width, height });
  }

  // Add edges to dagre
  for (const edge of edges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  // Run layout
  dagre.layout(dagreGraph);

  // Apply positions back to nodes
  return nodes.map((node) => {
    const dagreNode = dagreGraph.node(node.id);
    if (!dagreNode) return node;

    const width = node.measured?.width ?? opts.nodeWidth;
    const height = node.measured?.height ?? opts.nodeHeight;

    return {
      ...node,
      position: {
        x: dagreNode.x - width / 2,
        y: dagreNode.y - height / 2,
      },
    };
  });
}

/**
 * Calculate the bounding box of all nodes
 */
export function getNodesBounds(nodes: Node[]): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  if (nodes.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of nodes) {
    const width = node.measured?.width ?? 280;
    const height = node.measured?.height ?? 120;

    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + width);
    maxY = Math.max(maxY, node.position.y + height);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
